import React, { useEffect, useState } from "react";
import PageHero from "@/components/PageHero";
import NaturalNextSteps from "@/components/NaturalNextSteps";
import { api } from "@/lib/api";
import { ExternalLink } from "lucide-react";
import TopImage from "@/components/TopImage";
import useContent from "@/hooks/useContent";
import useSeo from "@/hooks/useSeo";
import { IMG } from "@/lib/images";

const seedFallback = [
  {
    id: "seed-1",
    title: "The space to think",
    description: "A brief reflection on how clarity — not urgency — moves us forward.",
    url: "#",
    category: "Article",
    image_url: null,
    published: true,
  },
  {
    id: "seed-2",
    title: "When patterns speak",
    description: "How recurring patterns quietly shape the results we're already creating.",
    url: "#",
    category: "Reflection",
    image_url: null,
    published: true,
  },
  {
    id: "seed-3",
    title: "Starting from where you are",
    description: "A short talk on beginning without needing everything figured out first.",
    url: "#",
    category: "Video",
    image_url: null,
    published: true,
  },
];

export default function Resources() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const t = useContent("resources");
  useSeo("resources", {
    title: "Resources — Quantum Results",
    description:
      "Articles, reflections, videos, and other resources designed to help you think more clearly.",
  });

  useEffect(() => {
    api
      .get("/resources")
      .then((r) => setItems(r.data.length ? r.data : seedFallback))
      .catch(() => setItems(seedFallback))
      .finally(() => setLoading(false));
  }, []);

  const categories = Array.from(new Set(items.map((i) => i.category || "Article")));

  return (
    <div data-testid="page-resources">
      <TopImage src={IMG.resources.hero.src} alt={IMG.resources.hero.alt} credit={IMG.resources.hero.credit} testId="resources-top-image" />
      <PageHero
        eyebrow={t("hero_eyebrow", "Resources")}
        title={t("hero_title", "A quiet library of ideas.")}
        lede={t("hero_lede", "Articles, reflections, videos, and other resources designed to help you think more clearly, see new possibilities, and continue moving forward at your own pace.")}
        testId="resources-hero"
      />

      <section className="container-narrow pb-24" data-testid="resources-list">
        {loading ? (
          <p className="text-brand-ink/50 text-[14px]">Loading…</p>
        ) : (
          <div className="space-y-16">
            {categories.map((cat) => (
              <div key={cat}>
                <div className="flex items-baseline justify-between mb-6 border-b border-brand-rule/70 pb-3">
                  <h2 className="font-serif text-[28px] text-brand-ink">{cat}</h2>
                  <span className="eyebrow">
                    {items.filter((i) => (i.category || "Article") === cat).length} entries
                  </span>
                </div>
                <ul className="grid gap-4 md:grid-cols-2">
                  {items
                    .filter((i) => (i.category || "Article") === cat)
                    .map((r) => {
                      const isFile = r.resource_kind === "file";
                      const primaryHref = isFile ? r.file_url : (r.url || "#");
                      return (
                      <li key={r.id}>
                        <div
                          data-testid={`resource-card-${r.id}`}
                          className="group block rounded-lg border border-brand-rule/70 bg-brand-cream p-6 h-full hover:border-brand-blue/50 transition-colors"
                        >
                          {r.image_url && !isFile && (
                            <div className="mb-4 overflow-hidden rounded-md aspect-[16/9] bg-brand-paper">
                              <img
                                src={r.image_url}
                                alt=""
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                                loading="lazy"
                              />
                            </div>
                          )}
                          <a
                            href={primaryHref}
                            target={isFile || (r.url && r.url !== "#") ? "_blank" : undefined}
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <h3 className="font-serif text-[22px] text-brand-ink leading-snug group-hover:text-brand-blue transition-colors">
                              {r.title}
                            </h3>
                            {r.description && (
                              <p className="mt-3 text-[14.5px] leading-relaxed text-brand-ink/70">
                                {r.description}
                              </p>
                            )}
                          </a>
                          {isFile ? (
                            <div className="mt-5 flex flex-col gap-2">
                              <a
                                href={r.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`resource-open-${r.id}`}
                                className="inline-flex items-center gap-2 text-[13px] text-brand-blue/90 group-hover:text-brand-blue"
                              >
                                Read online <ExternalLink size={13} />
                              </a>
                              <a
                                href={r.file_download_url}
                                data-testid={`resource-download-${r.id}`}
                                className="inline-flex items-center gap-1.5 text-[12px] text-brand-ink/50 hover:text-brand-blue"
                              >
                                Download <span aria-hidden>↓</span>
                              </a>
                            </div>
                          ) : (
                            <a
                              href={primaryHref}
                              target={r.url && r.url !== "#" ? "_blank" : undefined}
                              rel="noopener noreferrer"
                              data-testid={`resource-open-${r.id}`}
                              className="mt-5 inline-flex items-center gap-2 text-[13px] text-brand-blue/80 hover:text-brand-blue group-hover:text-brand-blue"
                            >
                              Open <ExternalLink size={13} />
                            </a>
                          )}
                        </div>
                      </li>
                      );
                    })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>

      <NaturalNextSteps
        items={[
          { to: "/pattern-reveal", label: "Pattern Reveal" },
          { to: "/weekly-quantum-edge", label: "Weekly Quantum Edge" },
          { to: "/contact", label: "Start a conversation" },
        ]}
      />
    </div>
  );
}
